/**
 * Erreur remontée par l'API, telle qu'elle arrive du serveur.
 *
 * Le back sérialise `{ code, message, regle?, requestId }`
 * (`apps/api/src/core/errors`). Le front **affiche** ce message, il ne le
 * réécrit pas : une règle métier reformulée ici divergerait du CCT au premier
 * ajustement (skill `frontend-react-afs` § Afficher une erreur métier).
 */

/** Codes stables, exposés par l'API. Le front peut brancher dessus. */
export type CodeErreurApi =
  | 'VALIDATION'
  | 'AUTHENTIFICATION'
  | 'AUTORISATION'
  | 'INTROUVABLE'
  | 'CONFLIT'
  | 'REGLE_METIER'
  | 'LIMITE'
  | 'RESEAU'

export class ErreurApi extends Error {
  readonly code: CodeErreurApi
  readonly statut: number
  /** Référence `RG-*` quand le refus vient d'une règle métier. */
  readonly regle: string | undefined
  /** À afficher au support : c'est la clé pour retrouver la trace serveur. */
  readonly requestId: string | undefined
  /** Un refus qui se lève par une confirmation : sa raison stable, et ce qu'il faut montrer. */
  readonly raison: string | undefined
  readonly details: Readonly<Record<string, unknown>> | undefined
  /** Sur un 429 : le délai en secondes que donne l'en-tête `Retry-After`, s'il y est. */
  readonly reessayerDansS: number | undefined

  // `| undefined` explicite plutôt que `?` : `exactOptionalPropertyTypes` est
  // actif (tsconfig.base.json), et refuse qu'on affecte `undefined` à une
  // propriété simplement optionnelle.
  constructor(params: {
    message: string
    code: CodeErreurApi
    statut: number
    regle?: string | undefined
    requestId?: string | undefined
    raison?: string | undefined
    details?: Readonly<Record<string, unknown>> | undefined
    reessayerDansS?: number | undefined
  }) {
    super(params.message)
    this.name = 'ErreurApi'
    this.code = params.code
    this.statut = params.statut
    this.regle = params.regle
    this.requestId = params.requestId
    this.raison = params.raison
    this.details = params.details
    this.reessayerDansS = params.reessayerDansS
  }
}

/**
 * Le réseau qui tombe n'est pas une exception sur les sites AFS : coupures
 * de courant et de liaison sont quotidiennes (CP-12). Ce cas a donc son
 * propre code, pour que l'écran puisse proposer « Réessayer » plutôt
 * qu'afficher un message d'erreur serveur qui n'existe pas.
 */
export function erreurReseau(): ErreurApi {
  return new ErreurApi({
    message: 'Serveur injoignable. Vérifiez la connexion, puis réessayez.',
    code: 'RESEAU',
    statut: 0,
  })
}

/**
 * Le serveur n'a pas répondu à temps (S77, C1) : la liaison du site est
 * tombée, le Wi-Fi est resté branché, les paquets se perdent. Pour une
 * écriture, rien ne dit qu'elle n'a pas été faite : on le dit, pour qu'elle
 * ne soit pas refaite à l'aveugle.
 */
export function erreurDelaiDepasse(ecriture: boolean): ErreurApi {
  return new ErreurApi({
    message: ecriture
      ? 'Le serveur ne répond pas. L’action a peut-être été enregistrée : vérifiez avant de la refaire.'
      : 'Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.',
    code: 'RESEAU',
    // 408 et non 0 : le délai est déjà écoulé, une nouvelle tentative le doublerait.
    statut: 408,
  })
}

export function estErreurApi(valeur: unknown): valeur is ErreurApi {
  return valeur instanceof ErreurApi
}

/**
 * La réponse du serveur ne correspond pas à ce que cette version de l'écran
 * attend : presque toujours un déploiement pendant que l'onglet (ou le cache
 * du navigateur) gardait l'ancienne version. Recette du 29/09 : la chargée
 * voyait à la connexion le détail brut de la validation, parce que le serveur
 * lui rendait des permissions que l'ancienne version ne connaissait pas.
 * Recharger suffit.
 */
export class ErreurVersionObsolete extends Error {
  constructor() {
    super('Une nouvelle version de la plateforme est disponible. Rechargez la page pour continuer.')
    this.name = 'ErreurVersionObsolete'
  }
}
